import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Mail, MapPin, Phone, ShieldCheck, Lock, CreditCard, Sparkles } from "lucide-react";
import NewsletterForm from "./NewsletterForm";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";

// Normalise une URL saisie par l'admin (accepte "facebook.com/xxx" ou "https://...")
const normalizeUrl = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  if (!s || s === "#") return null;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const SOCIAL_DEFS = [
  { key: "facebook", Icon: Facebook, tid: "social-facebook" },
  { key: "instagram", Icon: Instagram, tid: "social-instagram" },
  { key: "linkedin", Icon: Linkedin, tid: "social-linkedin" },
  { key: "youtube", Icon: Youtube, tid: "social-youtube" },
];

export default function Footer() {
  const { t } = useI18n();
  const [site, setSite] = useState({});

  useEffect(() => {
    const load = () => api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    load();
    window.addEventListener("lel-settings-updated", load);
    return () => window.removeEventListener("lel-settings-updated", load);
  }, []);

  const socials = SOCIAL_DEFS
    .map(({ key, Icon, tid }) => ({ href: normalizeUrl(site[key]), Icon, tid, key }))
    .filter(s => s.href);

  return (
    <footer className="deep-section relative overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            {site.logo_url ? (
              <img src={site.logo_url} alt="Logo" className="w-12 h-12 rounded-full object-cover border border-gold/40"/>
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gold to-warm-orange grid place-items-center">
                <span className="font-display text-deep-blue text-lg font-bold">L</span>
              </div>
            )}
            <div>
              <div className="font-display text-2xl">{site.logo_text || "Larmes en Lumière"}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80 mt-0.5">{t("footer.associationTag")}</div>
            </div>
          </div>
          <p className="text-ivory/70 max-w-md italic font-display text-lg leading-relaxed">« {t("footerTag")} »</p>
          {socials.length > 0 && (
            <div className="flex gap-3 mt-6" data-testid="footer-socials">
              {socials.map(({ href, Icon, tid, key }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={key}
                  className="w-10 h-10 rounded-full bg-white/10 grid place-items-center hover:bg-gold hover:text-deep-blue transition"
                  data-testid={tid}
                >
                  <Icon size={16}/>
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="uppercase tracking-[0.2em] text-gold text-xs mb-4">{t("footer.explore")}</h4>
          <ul className="space-y-2 text-sm text-ivory/80">
            <li><Link to="/a-propos" className="hover:text-gold">{t("footer.linkAbout")}</Link></li>
            <li><Link to="/nos-programmes" className="hover:text-gold">{t("footer.linkPrograms")}</Link></li>
            <li><Link to="/faire-un-don" className="hover:text-gold">{t("footer.linkDonate")}</Link></li>
            <li><Link to="/devenir-benevole" className="hover:text-gold">{t("footer.linkVolunteer")}</Link></li>
            <li><Link to="/devenir-ambassadeur" className="hover:text-gold">{t("footer.linkAmbassador")}</Link></li>
            <li><Link to="/entreprises-partenaires" className="hover:text-gold">{t("footer.linkPartners")}</Link></li>
            <li><Link to="/transparence" className="hover:text-gold">{t("footer.linkTransparency")}</Link></li>
            <li><Link to="/actualites" className="hover:text-gold">{t("footer.linkNews")}</Link></li>
            <li><Link to="/galerie" className="hover:text-gold">{t("footer.linkGallery")}</Link></li>
            <li><Link to="/parrainage" className="hover:text-gold">{t("footer.linkSponsor")}</Link></li>
            <li><Link to="/contact" className="hover:text-gold">{t("footer.linkContact")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="uppercase tracking-[0.2em] text-gold text-xs mb-4">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm text-ivory/80">
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 text-gold flex-shrink-0"/> <span>{t("footer.address")} : {site.address ? <span>{site.address}</span> : <span className="italic text-ivory/50">{t("footer.empty")}</span>}</span></li>
            <li className="flex items-start gap-2"><Phone size={14} className="mt-1 text-gold flex-shrink-0"/> <span>{t("footer.phone")} : {site.contact_phone ? <a href={`tel:${site.contact_phone.replace(/\s/g,'')}`} className="hover:text-gold">{site.contact_phone}</a> : <span className="italic text-ivory/50">{t("footer.empty")}</span>}</span></li>
            <li className="flex items-start gap-2"><Mail size={14} className="mt-1 text-gold flex-shrink-0"/> <a href={`mailto:${site.contact_email || 'contact@larmes-en-lumiere.org'}`} className="hover:text-gold break-all">{site.contact_email || 'contact@larmes-en-lumiere.org'}</a></li>
          </ul>
          <div className="mt-6">
            <NewsletterForm />
          </div>
          <div className="mt-6 text-xs text-ivory/50 space-y-1">
            <div>{t("footer.privacy")}</div>
            <div>{t("footer.cookies")}</div>
            <div>{t("footer.legal")}</div>
            <div>{t("footer.giving")}</div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 grid md:grid-cols-4 gap-3" data-testid="trust-badges">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
            <Sparkles className="text-gold flex-shrink-0" size={22}/>
            <div>
              <div className="text-xs font-semibold text-ivory">{t("footer.transparent")}</div>
              <div className="text-[10px] text-ivory/50">{t("footer.transparentSub")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="badge-gdpr">
            <ShieldCheck className="text-green-soft flex-shrink-0" size={22}/>
            <div>
              <div className="text-xs font-semibold text-ivory">{t("footer.gdpr")}</div>
              <div className="text-[10px] text-ivory/50">{t("footer.gdprSub")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="badge-https">
            <Lock className="text-warm-orange flex-shrink-0" size={22}/>
            <div>
              <div className="text-xs font-semibold text-ivory">{t("footer.https")}</div>
              <div className="text-[10px] text-ivory/50">{t("footer.httpsSub")}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="badge-stripe">
            <CreditCard className="text-gold flex-shrink-0" size={22}/>
            <div>
              <div className="text-xs font-semibold text-ivory">{t("footer.stripe")}</div>
              <div className="text-[10px] text-ivory/50">{t("footer.stripeSub")}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-ivory/50">
          <div>© {new Date().getFullYear()} Larmes en Lumière — {t("footer.rights")}</div>
        </div>
      </div>
    </footer>
  );
}
